import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box, Typography, Card, CardContent, Button, Chip, Dialog, DialogTitle,
  DialogContent, IconButton, CircularProgress, Alert, Badge,
  LinearProgress, Tooltip, Pagination,
} from "@mui/material";
import {
  Archive as ArchiveIcon,
} from "@mui/icons-material";
import {
  LocalShipping as ShippingIcon, PhotoCamera as PhotoIcon,
  CheckCircle as CheckIcon, Close as CloseIcon,
  Notifications as NotifIcon,
  Done as DoneIcon, Warning as WarnIcon, Navigation as NavigationIcon,
} from "@mui/icons-material";
import PhotoUpload from "./PhotoUpload";
import RouteMap from "./RouteMap";
import { useAuth } from "../contexts/AuthContext";
import { brandTokens } from "../theme";
import { ErrorState, NoShipments } from "./FeedbackStates";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

// ─── Types ───────────────────────────────────────────────────────────────────
interface ShipmentLocation {
  id: string;
  location_type: "pickup" | "dropoff" | "driver_change";
  sequence_order: number;
  name: string;
  address: string;
  contact_name?: string;
  contact_phone?: string;
  scheduled_date: string;
  scheduled_time: string;
  actual_date?: string;
  status: string;
  load_description?: string;
  latitude?: number;
  longitude?: number;
  order_no?: string;
  assigned_driver_id?: string;
}

interface ShipmentAsset {
  id: string;
  asset_name: string;
  asset_type: string;
  license_plate?: string;
}

interface ShipmentDocument {
  id: string;
  document_type: string;
  file_name: string;
  location_id?: string;
}

interface Shipment {
  id: string;
  tracking_number: string;
  description: string;
  status: string;
  driver_id: string;
  total_weight: number;
  total_volume: number;
  route_miles?: number;
  special_instructions?: string;
  created_at?: string;
  locations: ShipmentLocation[];
  assets: ShipmentAsset[];
  photos?: { id: string; photo_type: string; file_name: string }[];
  documents?: ShipmentDocument[];
}

interface Notification {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

// ─── Status Config ────────────────────────────────────────────────────────────
const STATUS: Record<string, { label: string; color: string; bg: string; border: string }> = {
  pending: { label: "Pending", color: "#92400E", bg: "#FEF3C7", border: brandTokens.amber },
  assigned: { label: "Assigned", color: "#4C1D95", bg: "#EDE9FE", border: "#8B5CF6" },
  picked_up: { label: "Picked Up", color: "#1E40AF", bg: "#DBEAFE", border: "#3B82F6" },
  in_progress: { label: "In Transit", color: "#1E40AF", bg: "#DBEAFE", border: "#3B82F6" },
  delivered: { label: "Delivered", color: "#065F46", bg: "#D1FAE5", border: "#10B981" },
  completed: { label: "Completed", color: "#065F46", bg: "#D1FAE5", border: "#10B981" },
  cancelled: { label: "Cancelled", color: "#991B1B", bg: "#FEE2E2", border: "#EF4444" },
};
const getStatus = (s: string) => STATUS[s.toLowerCase()] || { label: s, color: "#374151", bg: "#F3F4F6", border: "#9CA3AF" };

// ─── Next action logic ────────────────────────────────────────────────────────
function getNextAction(shipment: Shipment, location: ShipmentLocation) {
  const { status } = shipment;
  const locStatus = location.status;
  const isPickup = location.location_type === "pickup";
  const isDropoff = location.location_type === "dropoff";

  // Single-step pickup: "Pickup Complete" (gated by BOL upload in the UI)
  if ((status === "assigned" || status === "in_progress" || status === "picked_up") && isPickup && locStatus !== "completed") {
    return { label: "Pickup Complete", action: "complete_pickup", color: "#10B981" };
  }
  // Single-step delivery: "Delivery Complete" (gated by POD upload in the UI)
  if ((status === "in_progress" || status === "picked_up") && isDropoff && locStatus !== "completed") {
    return { label: "Delivery Complete", action: "complete_delivery", color: "#10B981" };
  }
  // Driver Change handoff
  if ((status === "in_progress" || status === "picked_up") && location.location_type === "driver_change" && locStatus !== "completed") {
    return { label: "Confirm Handoff", action: "complete_driver_change", color: "#10B981" };
  }
  return null;
}

// ─── Location Row ─────────────────────────────────────────────────────────────
function LocationRow({
  loc, idx, shipment, onAction, isLast, previousCompleted, onUploadSuccess
}: {
  loc: ShipmentLocation;
  idx: number;
  shipment: Shipment;
  onAction: (action: string, locationId: string) => void;
  isLast: boolean;
  previousCompleted: boolean;
  onUploadSuccess?: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const docRef = useRef<HTMLInputElement>(null);

  const hasBol = shipment.documents?.some(d => d.document_type === "bol" && (d.location_id == loc.id));
  const hasPod = shipment.documents?.some(d => d.document_type === "pod" && (d.location_id == loc.id));

  const nextAction = getNextAction(shipment, loc);
  const isCompleted = loc.status === "completed";
  const isPickup = loc.location_type === "pickup";
  const isDropoff = loc.location_type === "dropoff";
  const isDriverChange = loc.location_type === "driver_change";
  const isPickupAction = nextAction?.action === "complete_pickup";
  const isDeliveryAction = nextAction?.action === "complete_delivery";
  const pickupDisabled = isPickupAction && (!hasBol || !previousCompleted);
  const deliveryDisabled = isDeliveryAction && (!hasPod || !previousCompleted);

  // Assuming driver change is a simple button press for now, no doc required
  const changeDisabled = !previousCompleted;

  const handleFileUpload = async (file: File, docType: string) => {
    setUploading(true);
    try {
      const token = localStorage.getItem("driver_token");
      const fd = new FormData();
      fd.append("document", file);
      fd.append("document_type", docType);
      fd.append("notes", `${docType.toUpperCase()} uploaded at ${new Date().toLocaleString()}`);
      if (loc.id) fd.append("location_id", loc.id);
      const res = await fetch(`${API_URL}/api/shipments/${shipment.id}/documents`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        setUploadMsg(`${docType.toUpperCase()} uploaded successfully`);
        if (onUploadSuccess) onUploadSuccess();
      } else {
        setUploadMsg("Upload failed");
      }
    } catch {
      setUploadMsg("Upload failed — no connection");
    } finally {
      setUploading(false);
      setTimeout(() => setUploadMsg(null), 4000);
    }
  };

  // Determine step state
  const stepColor = isCompleted ? "#10B981" : (nextAction ? brandTokens.navy : "#94A3B8");

  return (
    <Box sx={{ display: "flex", gap: 0 }}>
      {/* Vertical connector */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 40, flexShrink: 0, pt: 0.5 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: stepColor, color: "#fff", fontWeight: 800, fontSize: "0.75rem", flexShrink: 0,
          boxShadow: isCompleted ? "0 0 0 3px rgba(16,185,129,0.2)" : nextAction ? "0 0 0 3px rgba(15,27,45,0.1)" : "none",
        }}>
          {isCompleted ? <CheckIcon sx={{ fontSize: 16 }} /> : idx + 1}
        </Box>
        {!isLast && (
          <Box sx={{ width: 2, flex: 1, bgcolor: isCompleted ? "#A7F3D0" : "#E2E8F0", minHeight: 20 }} />
        )}
      </Box>

      {/* Card content */}
      <Box sx={{
        flex: 1, mb: isLast ? 0 : 1.5,
        borderRadius: "12px", border: "1.5px solid",
        borderColor: isCompleted ? "#A7F3D0" : nextAction ? brandTokens.navy + "20" : "#E2E8F0",
        bgcolor: isCompleted ? "#F0FDF4" : "#fff",
        overflow: "hidden",
      }}>
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" fontWeight={700}>{loc.name || "Stop"}</Typography>
            <Chip
              label={isPickup ? "Pickup" : isDropoff ? "Delivery" : "Driver Change"}
              size="small"
              sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, bgcolor: isPickup ? brandTokens.navy : isDropoff ? brandTokens.amberDark : "#7C3AED", color: "#fff" }}
            />
            {isCompleted && <Chip label="Done" size="small" icon={<CheckIcon sx={{ fontSize: 12 }} />} sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, bgcolor: "#D1FAE5", color: "#065F46" }} />}
          </Box>
          {loc.scheduled_date && (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {new Date(loc.scheduled_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              {loc.scheduled_time && ` · ${loc.scheduled_time}`}
            </Typography>
          )}
        </Box>

        {/* Address */}
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>{loc.address}</Typography>
        </Box>

        {/* Order Number & Document */}
        {isPickup && (loc.order_no || shipment.documents?.some(d => d.document_type === "pickup_order" && d.location_id == loc.id)) && (
          <Box sx={{ px: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, borderTop: "1px dashed #E2E8F0", pt: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {loc.order_no && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>Order Number</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: brandTokens.navy }}>{loc.order_no}</Typography>
                </Box>
              )}
              {shipment.documents?.some(d => d.document_type === "pickup_order" && d.location_id == loc.id) && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PhotoIcon sx={{ fontSize: 13 }} />}
                  onClick={() => window.open(`${API_URL}/uploads/${shipment.documents!.find(d => d.document_type === "pickup_order" && d.location_id == loc.id)?.file_name}`, "_blank")}
                  sx={{ fontSize: "0.68rem", height: 26, bgcolor: "#E2E8F0", color: brandTokens.navy, boxShadow: "none", "&:hover": { bgcolor: "#CBD5E1", boxShadow: "none" }, mt: loc.order_no ? 1.8 : 0, px: 1.5, borderRadius: "6px" }}
                >
                  View Order
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Action area — only for active stops */}
        {!isCompleted && nextAction && (
          <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid rgba(0,0,0,0.06)", bgcolor: "#FAFBFC" }}>

            {isPickup && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {/* Step instruction */}
                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, fontSize: "0.7rem" }}>
                  {!previousCompleted ? "Complete previous stops first" : (!hasBol ? "Step 1: Take a photo of the BOL" : "Step 2: Confirm pickup is complete")}
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                  <Tooltip title={!previousCompleted ? "Complete previous stops first" : ""}>
                    <span style={{ display: "block", width: "100%" }}>
                      <Button
                        size="small"
                        disabled={!previousCompleted}
                        variant={hasBol ? "outlined" : "contained"}
                        startIcon={<PhotoIcon sx={{ fontSize: 18 }} />}
                        onClick={() => docRef.current?.click()}
                        sx={{
                          width: "100%", height: 60,
                          fontSize: "0.85rem", fontWeight: 700, borderRadius: "8px", px: 2,
                          ...(hasBol
                            ? { borderColor: "#10B981", color: "#10B981" }
                            : { bgcolor: brandTokens.navy, "&:hover": { bgcolor: brandTokens.navyLight } }),
                        }}
                      >
                        {hasBol ? "BOL Uploaded" : "Upload BOL"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={!previousCompleted ? "Complete previous stops first" : (pickupDisabled ? "Upload BOL photo first" : "")}>
                    <span style={{ display: "block", width: "100%" }}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={pickupDisabled}
                        startIcon={<DoneIcon sx={{ fontSize: 18 }} />}
                        onClick={() => onAction(nextAction.action, loc.id)}
                        sx={{
                          width: "100%", height: 60,
                          bgcolor: pickupDisabled ? "#CBD5E1" : "#10B981", fontSize: "0.85rem", fontWeight: 700, borderRadius: "8px", px: 2,
                          color: pickupDisabled ? "#94A3B8" : "#fff",
                          "&:hover": { bgcolor: "#059669" },
                          "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                        }}
                      >
                        Pickup Complete
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* ── DELIVERY FLOW — mirrors pickup ── */}
            {isDropoff && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, fontSize: "0.7rem" }}>
                  {!previousCompleted ? "Complete previous stops first" : (!hasPod ? "Step 1: Take a photo of the POD" : "Step 2: Confirm delivery is complete")}
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                  <Tooltip title={!previousCompleted ? "Complete previous stops first" : ""}>
                    <span style={{ display: "block", width: "100%" }}>
                      <Button
                        size="small"
                        disabled={!previousCompleted}
                        variant={hasPod ? "outlined" : "contained"}
                        startIcon={<PhotoIcon sx={{ fontSize: 18 }} />}
                        onClick={() => {
                          if (docRef.current) {
                            docRef.current.setAttribute("data-doc-type", "pod");
                            docRef.current.click();
                          }
                        }}
                        sx={{
                          width: "100%", height: 60,
                          fontSize: "0.85rem", fontWeight: 700, borderRadius: "8px", px: 2,
                          ...(hasPod
                            ? { borderColor: "#10B981", color: "#10B981" }
                            : { bgcolor: brandTokens.navy, "&:hover": { bgcolor: brandTokens.navyLight } }),
                          "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                        }}
                      >
                        {hasPod ? "POD Uploaded" : "Upload POD"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={!previousCompleted ? "Complete previous stops first" : (deliveryDisabled ? "Upload POD photo first" : "")}>
                    <span style={{ display: "block", width: "100%" }}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={deliveryDisabled}
                        startIcon={<DoneIcon sx={{ fontSize: 18 }} />}
                        onClick={() => onAction(nextAction!.action, loc.id)}
                        sx={{
                          width: "100%", height: 60,
                          bgcolor: deliveryDisabled ? "#CBD5E1" : "#10B981", fontSize: "0.85rem", fontWeight: 700, borderRadius: "8px", px: 2,
                          color: deliveryDisabled ? "#94A3B8" : "#fff",
                          "&:hover": { bgcolor: "#059669" },
                          "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                        }}
                      >
                        Delivery Complete
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* ── DRIVER CHANGE FLOW ── */}
            {isDriverChange && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, fontSize: "0.7rem" }}>
                  {!previousCompleted ? "Complete previous stops first" : "Confirm handoff to the next driver"}
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr" }, gap: 1 }}>
                  <Tooltip title={!previousCompleted ? "Complete previous stops first" : ""}>
                    <span style={{ display: "block", width: "100%" }}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={changeDisabled}
                        startIcon={<DoneIcon sx={{ fontSize: 18 }} />}
                        onClick={() => onAction(nextAction!.action, loc.id)}
                        sx={{
                          width: "100%", height: 60,
                          bgcolor: changeDisabled ? "#CBD5E1" : "#7C3AED", fontSize: "0.85rem", fontWeight: 700, borderRadius: "8px", px: 2,
                          color: changeDisabled ? "#94A3B8" : "#fff",
                          "&:hover": { bgcolor: "#6D28D9" },
                          "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                        }}
                      >
                        Confirm Handoff
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {uploading && <CircularProgress size={14} sx={{ mt: 0.5 }} />}
            {
              uploadMsg && (
                <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.5, display: "block", color: uploadMsg.includes("successfully") ? "#059669" : "#DC2626" }}>
                  {uploadMsg}
                </Typography>
              )
            }
          </Box>
        )
        }

        {/* Hidden file input */}
        <input ref={docRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            const dtype = e.target.getAttribute("data-doc-type") || (loc.location_type === "pickup" ? "bol" : "pod");
            if (f) handleFileUpload(f, dtype);
            e.target.removeAttribute("data-doc-type");
            e.target.value = "";
          }} />
      </Box>
    </Box>
  );
}

// ─── Shipment Card → Full Detail Sheet ────────────────────────────────────────
function ShipmentSheet({
  shipment,
  onStatusChange,
}: {
  shipment: Shipment;
  onStatusChange: (action: string, locationId?: string) => void;
}) {
  const cfg = getStatus(shipment.status);
  const [open, setOpen] = useState(false);
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [documents, setDocuments] = useState<any[]>(shipment.documents || []);
  const { user: driver } = useAuth();
  const driverId = driver?.driver_id;

  // Filter locations to only show those relevant to the current driver
  const locations = useMemo(() => {
    const rawArgs = [...(shipment.locations ?? [])].sort((a, b) => a.sequence_order - b.sequence_order);

    // Build effective drivers array (stops inherit assignment from previous stops)
    let currentDriver = shipment.driver_id;
    const effectiveDrivers = rawArgs.map((l) => {
      if (l.assigned_driver_id) {
        currentDriver = l.assigned_driver_id;
      }
      return currentDriver;
    });

    return rawArgs.filter((l, idx) => {
      const myEffectiveDriver = effectiveDrivers[idx];

      // 1. I am the effective driver for this stop
      if (myEffectiveDriver === driverId) return true;

      // 2. I am the driver arriving AT a handoff spot (the previous stop was mine)
      if (idx > 0 && l.location_type === "driver_change" && effectiveDrivers[idx - 1] === driverId) {
        return true;
      }

      // 3. I am the driver starting FROM a handoff spot (the next stop is mine)
      if (idx < rawArgs.length - 1 && l.location_type === "driver_change" && effectiveDrivers[idx + 1] === driverId) {
        return true;
      }

      return false;
    });
  }, [shipment.locations, shipment.driver_id, driverId]);

  const checkDocs = useCallback(async () => {
    try {
      const token = localStorage.getItem("driver_token");
      const res = await fetch(`${API_URL}/api/shipments/${shipment.id}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const docs = await res.json();
        setDocuments(docs);
      }
    } catch { /* ignore */ }
  }, [shipment.id]);

  // Check if documents already exist for this shipment
  useEffect(() => {
    checkDocs();
  }, [checkDocs]);

  // Progress
  const completed = locations.filter((l) => l.status === "completed").length;
  const progress = locations.length > 0 ? (completed / locations.length) * 100 : 0;

  return (
    <>
      {/* Card */}
      <Card
        sx={{
          borderLeft: `4px solid ${cfg.border}`,
          cursor: "pointer",
          transition: "transform 0.15s, box-shadow 0.15s",
          "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(15,27,45,0.12)" },
        }}
        onClick={() => setOpen(true)}
      >
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>Shipment #{shipment.tracking_number}</Typography>
            </Box>
            <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, height: 22, fontSize: "0.72rem" }} />
          </Box>

          {/* Progress bar */}
          {locations.length > 0 && shipment.status !== "assigned" && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                <Typography variant="caption" color="text.secondary">Route Progress</Typography>
                <Typography variant="caption" fontWeight={600}>{completed}/{locations.length} stops</Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 5, borderRadius: 5, bgcolor: "#E2E8F0", "& .MuiLinearProgress-bar": { bgcolor: cfg.border } }} />
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {shipment.route_miles && (
              <Box>
                <Typography variant="caption" color="text.secondary">Miles</Typography>
                <Typography variant="body2" fontWeight={600}>{Number(shipment.route_miles).toFixed(1)} mi</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Weight</Typography>
              <Typography variant="body2" fontWeight={600}>{Number(shipment.total_weight || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Stops</Typography>
              <Typography variant="body2" fontWeight={600}>{locations.length}</Typography>
            </Box>
            {shipment.status === "assigned" && (
              <Box>
                <Typography variant="caption" color="text.secondary">Next Step</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: "#1D4ED8" }}>Upload BOL → Complete Pickup</Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Full Detail Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="sm" 
        fullWidth
        sx={{
          "& .MuiDialog-container": { 
            alignItems: { xs: "flex-start", sm: "center" } 
          },
          "& .MuiDialog-paper": {
            margin: { xs: "12px", sm: "32px" },
            width: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" },
            borderRadius: "16px",
            maxHeight: "92vh"
          }
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{ bgcolor: brandTokens.navy, backgroundImage: `linear-gradient(135deg, ${brandTokens.navyLight} 0%, ${brandTokens.navy} 100%)`, p: 0 }}
        >
          <Box sx={{ px: 2.5, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff" }}>Shipment #{shipment.tracking_number}</Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.3, alignItems: "center" }}>
                <Chip label={cfg.label} size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: cfg.bg, color: cfg.color }} />
              </Box>
            </Box>
            <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: "rgba(255,255,255,0.7)" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {/* Progress strip */}
          {locations.length > 0 && (
            <LinearProgress variant="determinate" value={progress} sx={{ height: 3, bgcolor: "rgba(255,255,255,0.1)", "& .MuiLinearProgress-bar": { bgcolor: brandTokens.amber } }} />
          )}
        </DialogTitle>

        <DialogContent sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Quick info grid */}
          <Box sx={{ display: "grid", gridTemplateColumns: shipment.route_miles ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr", gap: 0.75, mt: 1 }}>
            {[
              ...(shipment.route_miles ? [{ label: "Miles", value: `${Number(shipment.route_miles).toFixed(1)} mi` }] : []),
              { label: "Weight", value: `${Number(shipment.total_weight || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs` },
              { label: "Stops", value: locations.length },
              { label: "Progress", value: `${completed}/${locations.length}` },
            ].map((i) => (
              <Box key={i.label} sx={{ p: 0.75, bgcolor: "#F8FAFC", borderRadius: "8px", textAlign: "center" }}>
                <Typography variant="h6" fontWeight={800} sx={{ color: brandTokens.navy, lineHeight: 1.1, fontSize: shipment.route_miles ? "0.85rem" : "1.1rem" }}>{i.value}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>{i.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Route Map */}
          {locations.some(l => l.latitude && l.longitude) && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5, display: "block" }}>Route Overview</Typography>
              <RouteMap locations={locations} height={180} />
            </Box>
          )}

          {/* Description */}
          {shipment.description && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>Load</Typography>
              <Typography variant="body2" sx={{ mt: 0.3, color: "text.primary" }}>{shipment.description}</Typography>
            </Box>
          )}

          {/* Special instructions */}
          {shipment.special_instructions && (
            <Box sx={{ p: 1.5, bgcolor: "#FFFBEB", borderRadius: "10px", border: "1px solid #FDE68A", display: "flex", gap: 1 }}>
              <WarnIcon sx={{ color: brandTokens.amber, fontSize: 18, flexShrink: 0, mt: 0.1 }} />
              <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: "#92400E" }}>Special Instructions</Typography>
                <Typography variant="body2" sx={{ mt: 0.25, color: "#78350F" }}>{shipment.special_instructions}</Typography>
              </Box>
            </Box>
          )}

          {/* Route Stops — the action heart */}
          {locations.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Route Stops
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {completed}/{locations.length} completed
                </Typography>
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {locations
                  .sort((a, b) => a.sequence_order - b.sequence_order)
                  .map((loc, idx, arr) => {
                    const previousCompleted = idx === 0 || arr[idx - 1].status === "completed";
                    return (
                      <LocationRow
                        key={loc.id}
                        loc={loc}
                        idx={idx}
                        shipment={{ ...shipment, documents }}
                        isLast={idx === locations.length - 1}
                        previousCompleted={previousCompleted}
                        onUploadSuccess={checkDocs}
                        onAction={(action, locId) => {
                          onStatusChange(action, locId);
                        }}
                      />
                    );
                  })}
              </Box>
            </Box>
          )}

          {/* Assets */}
          {(shipment.assets ?? []).length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Assigned Equipment</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {shipment.assets.map((a) => (
                  <Chip key={a.id} icon={<ShippingIcon sx={{ fontSize: 13 }} />} size="small"
                    label={`${a.asset_name}${a.license_plate ? ` · ${a.license_plate}` : ""}`} />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #E2E8F0", display: "flex", gap: 1.5 }}>
          <Button 
            size="large" variant="outlined" onClick={() => setOpen(false)} 
            sx={{ flex: 1, height: 52, borderRadius: "8px", fontWeight: 700, borderColor: "#CBD5E1", color: "#475569", "&:hover": { borderColor: "#94A3B8", bgcolor: "#F8FAFC" } }}
          >
            Close
          </Button>
          <Button
            size="large" variant="contained" color="secondary"
            startIcon={<PhotoIcon sx={{ fontSize: 18 }} />}
            onClick={() => { setPhotoUploadOpen(true); setOpen(false); }}
            sx={{ color: brandTokens.navy, flex: 1, height: 52, borderRadius: "8px", fontWeight: 700, fontSize: "0.85rem", whiteSpace: "nowrap" }}
          >
            Add Photo
          </Button>
        </Box>
      </Dialog>

      <PhotoUpload
        open={photoUploadOpen}
        onClose={() => setPhotoUploadOpen(false)}
        shipmentId={shipment.id}
      />
    </>
  );
}

// ─── Main Dashboard ────────────────────────────────────────────────────────────
const ARCHIVE_PAGE_SIZE = 10;

const Dashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const { user: driver } = useAuth();
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number; acc: number } | null>(null);
  const [showArchive, setShowArchive] = useState(false);
  const [archivePage, setArchivePage] = useState(1);
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  useEffect(() => {
    // Determine if we need to ask for push notifications
    if ("Notification" in window) {
      if (Notification.permission === "default") {
        // slight delay to let dashboard render
        const timer = setTimeout(() => setShowPushPrompt(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const handleRequestPush = () => {
    if ("Notification" in window) {
      Notification.requestPermission().then(() => {
        setShowPushPrompt(false);
      });
    } else {
      setShowPushPrompt(false);
    }
  };

  const { data: shipments = [], isLoading, error } = useQuery<Shipment[]>({
    queryKey: ["shipments"],
    queryFn: async () => {
      const token = localStorage.getItem("driver_token");
      const res = await fetch(`${API_URL}/api/shipments/driver/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed to fetch shipments");
      return res.json();
    },
    refetchInterval: 30000,
  });

  const { data: notifications = [], isFetched: isNotificationsFetched } = useQuery<Notification[]>({
    queryKey: ["notifications"],
    queryFn: async () => {
      const token = localStorage.getItem("driver_token");
      const res = await fetch(`${API_URL}/api/shipments/notifications/driver/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!driver,
    refetchInterval: 60000,
  });

  const unreadCount = notifications.filter((n: Notification) => !n.is_read).length;
  const prevUnreadCountRef = useRef(unreadCount);
  const hasInitializedNotifications = useRef(false);

  useEffect(() => {
    if (!isNotificationsFetched) return;

    if (!hasInitializedNotifications.current) {
      hasInitializedNotifications.current = true;
      prevUnreadCountRef.current = unreadCount;
      return;
    }

    if (unreadCount > prevUnreadCountRef.current) {
      // Play ding sound
      try {
        const audio = new Audio("/ping.mp3");
        audio.play().catch(e => console.log("Audio play blocked by browser:", e));
      } catch (e) {
        console.error("Failed to play notification audio:", e);
      }

      // Fire a push notification
      if ("Notification" in window && Notification.permission === "granted") {
        try {
          new window.Notification("New Shipment", {
            body: "A new shipment has been assigned to you.",
            icon: "/logo.png"
          });
        } catch (e) {
          console.error("Failed to showcase notification:", e);
        }
      }
    }
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount, isNotificationsFetched]);

  const statusMutation = useMutation({
    mutationFn: async ({ shipmentId, action, locationId, coords }: { shipmentId: string; action: string; locationId?: string; coords?: { lat: number; lng: number } }) => {
      const token = localStorage.getItem("driver_token");

      const payload: any = { action, location_id: locationId };
      // If the action indicates a completion (picked_up, complete_delivery, complete_pickup), freeze current exact time & timezone
      if (['picked_up', 'complete_delivery', 'complete_pickup', 'complete_driver_change'].includes(action)) {
        payload.completed_at = new Date().toISOString();
        payload.completed_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (coords) {
          payload.completed_latitude = coords.lat;
          payload.completed_longitude = coords.lng;
        }
      }

      const res = await fetch(`${API_URL}/api/shipments/${shipmentId}/driver-status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || "Failed to update status"); }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["shipments"] }),
  });

  const handleStatusChange = (shipmentId: string, action: string, locationId?: string) => {
    // If it's a completion action, try to grab fresh GPS immediately
    if (['complete_pickup', 'complete_delivery', 'complete_driver_change'].includes(action)) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            setCurrentLocation({ ...coords, acc: pos.coords.accuracy });
            statusMutation.mutate({ shipmentId, action, locationId, coords });
          },
          () => {
            // Fallback to state or no coords
            statusMutation.mutate({ shipmentId, action, locationId, coords: currentLocation ? { lat: currentLocation.lat, lng: currentLocation.lng } : undefined });
          },
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
        return;
      }
    }
    // Default flow without explicitly requested coordinates
    statusMutation.mutate({ shipmentId, action, locationId });
  };

  const startGPS = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy };
        setCurrentLocation(loc);
        const token = localStorage.getItem("driver_token");
        const active = shipments.filter((s) => s.status === "in_progress" || s.status === "assigned");
        active.forEach((s) =>
          fetch(`${API_URL}/api/shipments/${s.id}/location`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ latitude: loc.lat, longitude: loc.lng, accuracy: loc.acc, timestamp: Date.now() }),
          })
        );
      },
      () => { /* location access denied or failed */ },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => { startGPS(); const iv = setInterval(startGPS, 10 * 60 * 1000); return () => clearInterval(iv); }, []);

  // Split shipments: active (non-delivered) vs archived (delivered/completed), newest first
  const sortNewest = (a: Shipment, b: Shipment) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA; // newest first
  };
  const activeShipments = useMemo(() =>
    shipments.filter(s => !['delivered', 'completed'].includes(s.status)).sort(sortNewest),
    [shipments]
  );
  const archivedShipments = useMemo(() =>
    shipments.filter(s => ['delivered', 'completed'].includes(s.status)).sort(sortNewest),
    [shipments]
  );
  const archivePageCount = Math.ceil(archivedShipments.length / ARCHIVE_PAGE_SIZE);
  const archivedPage = archivedShipments.slice((archivePage - 1) * ARCHIVE_PAGE_SIZE, archivePage * ARCHIVE_PAGE_SIZE);

  const active = shipments.filter((s) => ["in_progress", "assigned"].includes(s.status));
  const unread = shipments.filter((s) => s.status === "assigned").length;

  const getNextStop = (shipment: Shipment) => {
    if (!driver || !shipment.locations) return null;
    const rawArgs = [...shipment.locations].sort((a, b) => a.sequence_order - b.sequence_order);
    let currentDriver = shipment.driver_id;
    const effectiveDrivers = rawArgs.map((l) => {
      if (l.assigned_driver_id) currentDriver = l.assigned_driver_id;
      return currentDriver;
    });

    return rawArgs.find((l, idx) => {
      if (l.status === 'completed') return false;
      const myEffectiveDriver = effectiveDrivers[idx];
      if (myEffectiveDriver === (driver as any).driver_id) return true;
      if (idx > 0 && l.location_type === "driver_change" && effectiveDrivers[idx - 1] === (driver as any).driver_id) return true;
      if (idx < rawArgs.length - 1 && l.location_type === "driver_change" && effectiveDrivers[idx + 1] === (driver as any).driver_id) return true;
      return false;
    });
  };

  if (isLoading) return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh", flexDirection: "column", gap: 2 }}>
      <CircularProgress sx={{ color: brandTokens.amber }} size={40} />
      <Typography color="text.secondary">Loading your shipments...</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <ErrorState
        title="Couldn't load shipments"
        message={(error as Error).message || "Something went wrong. Please check your connection and try again."}
        onRetry={() => queryClient.invalidateQueries({ queryKey: ["shipments"] })}
      />
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
            {driver ? `Hey, ${(driver as any).first_name} 👋` : "Driver Dashboard"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {active.length > 0 ? `${active.length} active shipment${active.length > 1 ? "s" : ""}` : "No active shipments"}
          </Typography>
        </Box>
        {unread > 0 && (
          <Badge badgeContent={unread} color="error">
            <NotifIcon sx={{ color: brandTokens.amber }} />
          </Badge>
        )}
      </Box>

      {/* Stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5, mb: 3 }}>
        {[
          { label: "Total", value: shipments.length, color: brandTokens.navy },
          { label: "Active", value: active.length, color: "#3B82F6" },
          { label: "Delivered", value: shipments.filter((s) => s.status === "delivered" || s.status === "completed").length, color: "#10B981" },
        ].map((s) => (
          <Card key={s.label} sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="h4" fontWeight={800} sx={{ color: s.color, lineHeight: 1 }}>{s.value}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>{s.label}</Typography>
          </Card>
        ))}
      </Box>

      {/* Next Stop Section */}
      {activeShipments.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            Next Stop
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {activeShipments.map(shipment => {
              const nextStop = getNextStop(shipment);
              if (!nextStop) return null;
              
              const mapUrl = `https://maps.google.com/?q=${encodeURIComponent(nextStop.address)}`;
              
              return (
                <Card key={`next-stop-${shipment.id}`} sx={{ borderLeft: `4px solid ${brandTokens.amber}` }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase" }}>
                          Shipment #{shipment.tracking_number}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25, pr: 2 }}>
                          {nextStop.address}
                        </Typography>
                      </Box>
                      <Button 
                        variant="contained" 
                        size="small" 
                        endIcon={<NavigationIcon sx={{ fontSize: 16 }} />}
                        onClick={() => window.open(mapUrl, "_blank", "noopener,noreferrer")}
                        sx={{ 
                          bgcolor: brandTokens.navy, 
                          color: "#fff",
                          "&:hover": { bgcolor: brandTokens.navyLight },
                          borderRadius: "8px", 
                          textTransform: "none",
                          minWidth: 'auto',
                          fontWeight: 600,
                          px: 2
                        }}
                      >
                        Navigate
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Active Shipments */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="subtitle1" fontWeight={700}>
          My Shipments
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({activeShipments.length})</Typography>
        </Typography>
        {statusMutation.isPending && <CircularProgress size={16} />}
      </Box>

      {statusMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{(statusMutation.error as Error).message}</Alert>
      )}

      {activeShipments.length === 0 && archivedShipments.length === 0 ? (
        <NoShipments />
      ) : activeShipments.length === 0 ? (
        <Card sx={{ p: 3, textAlign: "center", mb: 3 }}>
          <CheckIcon sx={{ fontSize: 40, color: "#10B981", mb: 1 }} />
          <Typography variant="body1" fontWeight={600} color="text.secondary">All shipments delivered!</Typography>
          <Typography variant="caption" color="text.disabled">Check the archive below for completed loads</Typography>
        </Card>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          {activeShipments.map((shipment) => (
            <ShipmentSheet
              key={shipment.id}
              shipment={shipment}
              onStatusChange={(action, locationId) => {
                handleStatusChange(shipment.id, action, locationId);
              }}
            />
          ))}
        </Box>
      )}

      {/* Archive Section */}
      {archivedShipments.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, cursor: "pointer" }}
            onClick={() => setShowArchive(!showArchive)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ArchiveIcon sx={{ fontSize: 20, color: "text.secondary" }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Archive
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({archivedShipments.length})</Typography>
              </Typography>
            </Box>
            <Button size="small" variant="text" sx={{ fontSize: "0.72rem", textTransform: "none", color: "text.secondary" }}>
              {showArchive ? "Hide" : "Show"}
            </Button>
          </Box>

          {showArchive && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {archivedPage.map((shipment) => (
                <ShipmentSheet
                  key={shipment.id}
                  shipment={shipment}
                  onStatusChange={(action, locationId) => {
                    handleStatusChange(shipment.id, action, locationId);
                  }}
                />
              ))}

              {/* Pagination */}
              {archivePageCount > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <Pagination
                    count={archivePageCount}
                    page={archivePage}
                    onChange={(_, p) => setArchivePage(p)}
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        fontWeight: 600,
                        "&.Mui-selected": { bgcolor: brandTokens.navy, color: "#fff" },
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Push Notification Request Dialog */}
      <Dialog open={showPushPrompt} onClose={() => setShowPushPrompt(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <NotifIcon sx={{ color: brandTokens.navy, fontSize: 24 }} />
          Enable Push Notifications
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1, lineHeight: 1.6 }}>
            We want to use push notifications to alert you of new shipments and related functions, strictly regarding your work.
          </Typography>
        </DialogContent>
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={() => setShowPushPrompt(false)} sx={{ color: "text.secondary" }}>
            Not Now
          </Button>
          <Button variant="contained" onClick={handleRequestPush} sx={{ bgcolor: brandTokens.navy, "&:hover": { bgcolor: brandTokens.navyLight } }}>
            Allow Notifications
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
